import { useEffect, useState } from "react";
import StaffNav from "./managernav";

const API_BASE = "https://api.insightos.co.uk/api";

const initialFormState = {
  StaffId: "",
  StaffName: "",
  Status: "planned",
  StartDate: "",
  ExpectedReturnDate: "",
  ActualReturnDate: "",
  PayrollHandledBy: "accountant",
  PayrollNotes: "",
  CompanyPolicyApplies: true,
  EnhancedPayApplies: false,
  PolicyNotes: "",
  HolidayAccrues: true,
  HolidayNotes: "",
  SystemAccessSuspended: true,
  BillingSuspended: true,
  AccountantConfirmed: false,
  AccountantNotes: "",
};

const statusBadge = {
  planned: "text-sky-700 bg-sky-50",
  active: "text-emerald-700 bg-emerald-50",
  ended: "text-gray-700 bg-gray-100",
};

const payrollOptions = [
  { value: "accountant", label: "Internal Accountant" },
  { value: "payroll_provider", label: "Payroll Provider" },
  { value: "other", label: "Other" },
];

const statusOptions = [
  { value: "planned", label: "Planned" },
  { value: "active", label: "Active" },
  { value: "ended", label: "Ended" },
];

const boolToLabel = (value) => (value ? "Yes" : "No");

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (error) {
    return value;
  }
};

const normalizeBoolean = (value, fallback = false) => {
  if (value === undefined || value === null || value === "") return fallback;
  return value === true || value === 1 || value === "1";
};

export default function Maternity({ embedded = false, prefillStaff = null }) {
  const [leaves, setLeaves] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [policyDetails, setPolicyDetails] = useState(null);

  useEffect(() => {
    Promise.all([fetchStaff(), fetchLeaves(), fetchPolicies()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!prefillStaff || !prefillStaff.id || editingId) return;
    setSelectedStaffId(String(prefillStaff.id));
    setFormData((prev) => ({
      ...prev,
      StaffId: String(prefillStaff.id),
      StaffName: prefillStaff.Name || prev.StaffName,
    }));
  }, [prefillStaff, editingId]);

  useEffect(() => {
    if (!policyDetails?.MaternityPolicy || editingId) return;
    setFormData((prev) => {
      if (prev.PayrollNotes) return prev;
      return { ...prev, PayrollNotes: policyDetails.MaternityPolicy };
    });
  }, [policyDetails, editingId]);

  const fetchStaff = async () => {
    try {
      const response = await fetch(`${API_BASE}/staff`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Unable to load staff");
      setStaffOptions(data.data || []);
    } catch (err) {
      console.error(err);
      setError("Unable to load staff list. Please refresh.");
    }
  };

  const fetchLeaves = async () => {
    try {
      const response = await fetch(`${API_BASE}/maternity`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Unable to load maternity data");
      setLeaves(data.data || []);
    } catch (err) {
      console.error(err);
      setError("Unable to load maternity leave records. Please refresh.");
    }
  };

  const fetchPolicies = async () => {
    try {
      const response = await fetch(`${API_BASE}/policies`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Unable to load policies");
      const policy = Array.isArray(data.data) ? data.data[0] : null;
      setPolicyDetails(policy || null);
      return policy;
    } catch (err) {
      console.error(err);
      setError((prev) => prev || "Unable to load maternity policy. Please refresh.");
      return null;
    }
  };

  const resetForm = () => {
    setFormData({
      ...initialFormState,
      PayrollNotes: policyDetails?.MaternityPolicy || initialFormState.PayrollNotes || "",
    });
    setSelectedStaffId(prefillStaff?.id ? String(prefillStaff.id) : "");
    setEditingId(null);
  };

  const openForm = () => {
    resetForm();
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleStaffChange = (event) => {
    const { value } = event.target;
    setSelectedStaffId(value);
    const selected = staffOptions.find((person) => String(person.id) === value);
    setFormData((prev) => ({
      ...prev,
      StaffId: value,
      StaffName: selected?.Name || "",
    }));
  };

  const handleFieldChange = (event) => {
    const { name, type, value, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const buildPayload = () => ({
    ...formData,
    CompanyPolicyApplies: formData.CompanyPolicyApplies ? 1 : 0,
    EnhancedPayApplies: formData.EnhancedPayApplies ? 1 : 0,
    HolidayAccrues: formData.HolidayAccrues ? 1 : 0,
    SystemAccessSuspended: formData.SystemAccessSuspended ? 1 : 0,
    BillingSuspended: formData.BillingSuspended ? 1 : 0,
    AccountantConfirmed: formData.AccountantConfirmed ? 1 : 0,
    PayrollNotes: formData.PayrollNotes || null,
    PolicyNotes: formData.PolicyNotes || null,
    HolidayNotes: formData.HolidayNotes || null,
    AccountantNotes: formData.AccountantNotes || null,
    ActualReturnDate: formData.ActualReturnDate || null,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const staffId = formData.StaffId || selectedStaffId;
      if (!staffId) throw new Error("Please select a staff member");
      if (!formData.StartDate) throw new Error("Please provide a start date");
      if (!formData.ExpectedReturnDate) throw new Error("Please provide an expected return date");

      const resolvedStaff = staffOptions.find((person) => String(person.id) === String(staffId));
      const payload = {
        ...buildPayload(),
        StaffId: staffId,
        StaffName: formData.StaffName || resolvedStaff?.Name || "",
      };
      const url = editingId ? `${API_BASE}/maternityedit/${editingId}` : `${API_BASE}/addmaternity`;
      const method = editingId ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Unable to save maternity leave");

      await fetchLeaves();
      closeForm();
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to save maternity leave");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setFormOpen(true);
    setSelectedStaffId(String(record.StaffId));
    setFormData({
      StaffId: String(record.StaffId),
      StaffName: record.StaffName || "",
      Status: record.Status || "planned",
      StartDate: record.StartDate ? record.StartDate.slice(0, 10) : "",
      ExpectedReturnDate: record.ExpectedReturnDate ? record.ExpectedReturnDate.slice(0, 10) : "",
      ActualReturnDate: record.ActualReturnDate ? record.ActualReturnDate.slice(0, 10) : "",
      PayrollHandledBy: record.PayrollHandledBy || "accountant",
      PayrollNotes: record.PayrollNotes || "",
      CompanyPolicyApplies: normalizeBoolean(record.CompanyPolicyApplies, true),
      EnhancedPayApplies: normalizeBoolean(record.EnhancedPayApplies, false),
      PolicyNotes: record.PolicyNotes || "",
      HolidayAccrues: normalizeBoolean(record.HolidayAccrues, true),
      HolidayNotes: record.HolidayNotes || "",
      SystemAccessSuspended: normalizeBoolean(record.SystemAccessSuspended, true),
      BillingSuspended: normalizeBoolean(record.BillingSuspended, true),
      AccountantConfirmed: normalizeBoolean(record.AccountantConfirmed, false),
      AccountantNotes: record.AccountantNotes || "",
    });
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete maternity leave record for ${record.StaffName}?`)) return;
    setError("");
    try {
      const response = await fetch(`${API_BASE}/maternitydelete/${record.id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Unable to delete record");
      await fetchLeaves();
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to delete record");
    }
  };

  const handleToggleActive = async (record) => {
    const activate = record.Status !== "active";
    const candidateReturnDate = record.ActualReturnDate || record.ExpectedReturnDate;

    if (activate && !candidateReturnDate) {
      setError("Set an actual or expected return date before activating maternity leave.");
      return;
    }

    setTogglingId(record.id);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/maternityactive`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          id: record.id,
          active: activate,
          actualReturnDate: candidateReturnDate?.slice(0, 10) || null,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Unable to update status");
      await fetchLeaves();
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to update status");
    } finally {
      setTogglingId(null);
    }
  };

  const renderTable = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20 text-gray-500">
          Loading maternity leave records...
        </div>
      );
    }

    if (!leaves.length) {
      return (
        <div className="py-16 text-center">
          <p className="text-lg font-semibold text-gray-900">No maternity leave records yet</p>
          <p className="mt-2 text-gray-500">Use "Add Leave" to capture your first entry.</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Staff</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Dates</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Payroll</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Policies</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {leaves.map((record) => (
              <tr key={record.id} className="hover:bg-gray-50">
                <td className="px-4 py-4 align-top">
                  <div className="font-semibold text-gray-900">{record.StaffName}</div>
                  <div className="text-sm text-gray-500">ID: {record.StaffId}</div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-600 align-top">
                  <div>Start: {formatDate(record.StartDate)}</div>
                  <div>Expected: {formatDate(record.ExpectedReturnDate)}</div>
                  <div>Actual: {formatDate(record.ActualReturnDate)}</div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-600 align-top">
                  <div className="font-medium capitalize">{(record.PayrollHandledBy || "accountant").replace("_", " ")}</div>
                  <div className="text-xs text-gray-500">{record.PayrollNotes || "No notes"}</div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-600 align-top space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                      Policy: {boolToLabel(record.CompanyPolicyApplies)}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                      Enhanced Pay: {boolToLabel(record.EnhancedPayApplies)}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                      Holiday: {boolToLabel(record.HolidayAccrues)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-700">
                      Access: {boolToLabel(record.SystemAccessSuspended)}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700">
                      Billing: {boolToLabel(record.BillingSuspended)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-gray-600 align-top">
                  <div className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusBadge[record.Status] || statusBadge.planned}`}>
                    {record.Status || "planned"}
                  </div>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full items-center justify-center rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                    onClick={() => handleToggleActive(record)}
                    disabled={togglingId === record.id}
                  >
                    {togglingId === record.id ? "Updating..." : record.Status === "active" ? "Mark as Ended" : "Activate"}
                  </button>
                </td>
                <td className="px-4 py-4 text-sm text-right align-top">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(record)}
                      className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(record)}
                      className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const formSection = (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {editingId ? "Update Maternity Leave" : "Add Maternity Leave"}
          </h2>
          <p className="text-sm text-gray-500">
            Capture leave details, payroll handling, and access requirements.
          </p>
        </div>
        <button
          type="button"
          onClick={closeForm}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
        >
          Close
        </button>
      </div>
      <form className="p-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-gray-700">Staff Member</label>
            <select
              value={selectedStaffId}
              onChange={handleStaffChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
              required
            >
              <option value="">Select staff member</option>
              {staffOptions.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.Name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              name="Status"
              value={formData.Status}
              onChange={handleFieldChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              name="StartDate"
              value={formData.StartDate}
              onChange={handleFieldChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Expected Return Date</label>
            <input
              type="date"
              name="ExpectedReturnDate"
              value={formData.ExpectedReturnDate}
              onChange={handleFieldChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Actual Return Date</label>
            <input
              type="date"
              name="ActualReturnDate"
              value={formData.ActualReturnDate}
              onChange={handleFieldChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Payroll Handled By</label>
            <select
              name="PayrollHandledBy"
              value={formData.PayrollHandledBy}
              onChange={handleFieldChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
            >
              {payrollOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-gray-700">Payroll Notes</label>
            <textarea
              name="PayrollNotes"
              rows={3}
              value={formData.PayrollNotes}
              onChange={handleFieldChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
              placeholder="Dependencies, payroll provider details, etc."
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Policy Notes</label>
            <textarea
              name="PolicyNotes"
              rows={3}
              value={formData.PolicyNotes}
              onChange={handleFieldChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
              placeholder="Clarify how policies apply during leave."
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Holiday Notes</label>
            <textarea
              name="HolidayNotes"
              rows={3}
              value={formData.HolidayNotes}
              onChange={handleFieldChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
              placeholder="Explain how holiday accrues."
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Accountant Notes</label>
            <textarea
              name="AccountantNotes"
              rows={3}
              value={formData.AccountantNotes}
              onChange={handleFieldChange}
              className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
              placeholder="Hand-off details or confirmations."
            />
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          {[
            { name: "CompanyPolicyApplies", label: "Company policy applies" },
            { name: "EnhancedPayApplies", label: "Enhanced pay applies" },
            { name: "HolidayAccrues", label: "Holiday continues to accrue" },
            { name: "SystemAccessSuspended", label: "Suspend system access" },
            { name: "BillingSuspended", label: "Suspend billing/licensing" },
            { name: "AccountantConfirmed", label: "Accountant confirmed" },
          ].map((field) => (
            <label key={field.name} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                name={field.name}
                checked={!!formData[field.name]}
                onChange={handleFieldChange}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
              />
              {field.label}
            </label>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={closeForm}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting ? "Saving..." : editingId ? "Update Leave" : "Save Leave"}
          </button>
        </div>
      </form>
    </div>
  );

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white/70 px-6 py-5 shadow-sm">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Maternity Leave</h1>
        <p className="text-sm text-gray-600">Track leave timelines, payroll ownership, and system access in one place.</p>
      </div>
      <button
        type="button"
        onClick={openForm}
        className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
      >
        Add Leave
      </button>
    </div>
  );

  const alert = error ? (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {error}
    </div>
  ) : null;

  const layout = (
    <div className="space-y-6">
      {header}
      {alert}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        {renderTable()}
      </div>
      {formOpen && formSection}
    </div>
  );

  if (embedded) {
    return layout;
  }

  return (
    <>
      <div className="min-h-screen bg-slate-50 py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {layout}
        </div>
      </div>
    </>
  );
}
